/*
 * PROD-7828 R8 — pure adapters: the goserver Additional-payments payloads (the contract of record)
 * -> view models. The JSX renders only what these return; adapters.test.js replays the golden
 * fixtures, so a wire-shape drift fails here instead of on screen.
 */
import { EM_DASH, api_error, fmt, fmt_signed, month_label, num } from '../format';
import {
  CARRY_FORWARD,
  ENTRY_TYPE,
  ERROR,
  LIMITS,
  LIST_STATUS,
  ROW_ACTION,
  SOURCE,
  WARNING,
  is_pay_month_in_window
} from './params';

// Whole rupees like L1, but paise are never hidden when the stored amount carries them.
export const amount_text = (amount) => {
  const n = num(amount);
  return fmt_signed(n, Number.isInteger(n) ? 0 : 2);
};

export const SOURCE_LABELS = {
  [SOURCE.MANUAL]: 'Manual',
  [SOURCE.CSV]: 'CSV upload',
  [SOURCE.SYSTEM]: 'System carry-forward',
  [SOURCE.ADJUSTMENT]: 'System adjustment'
};

export const source_label = (row) => SOURCE_LABELS[row && row.source] || (row && row.source) || '';

export const is_system_row = (row) => !!row && row.source === CARRY_FORWARD.SOURCE;

// Badge colour only: SYSTEM (D13 carry-forward) and ADJUSTMENT (A-6c) both post without an Ops hand.
export const is_system_posted = (row) =>
  !!row && (row.source === SOURCE.SYSTEM || row.source === SOURCE.ADJUSTMENT);

// The carry-forward shares its category with Ops' "Previous month payment adjustment" (Q6); the
// source, with the month it came from, is what tells the two apart wherever an entry is listed.
export const source_text = (row) =>
  is_system_row(row) && row.source_pay_month
    ? `${source_label(row)} · from ${month_label(row.source_pay_month)}`
    : source_label(row);

export const ENTRY_TYPE_LABELS = {
  [ENTRY_TYPE.ADDITION]: 'Addition',
  [ENTRY_TYPE.DEDUCTION]: 'Deduction'
};

// CD-59: a person reads by name; the uuid stays as the tooltip, and stands in when no name came.
export const who_view = (name, uuid) => ({ text: name || uuid || EM_DASH, title: uuid || '' });

const STATUS_LABELS = { [LIST_STATUS.ACTIVE]: 'Active', [LIST_STATUS.REMOVED]: 'Removed' };

const has_action = (row, action) =>
  !!row &&
  !!row.editable &&
  Array.isArray(row.editable.actions) &&
  row.editable.actions.indexOf(action) !== -1;

// CD-112: a carry-forward a reopen removed is bookkeeping, not an Ops decision, and reads as such.
const reversed_text = (row) =>
  is_system_row(row) &&
  row.status === LIST_STATUS.REMOVED &&
  row.removed_by === CARRY_FORWARD.ACTOR_REOPEN
    ? `Reversed when the month was reopened${row.removed_at_ist ? ` · ${row.removed_at_ist}` : ''}`
    : '';

// Actions come ONLY from the server's editable.actions — a closed/REVIEW month, a removed row and a
// SYSTEM row each arrive with the right subset and a why_not the UI shows instead of a button.
export const row_view = (row) => {
  const added = who_view(row.added_by_name, row.added_by);
  const removed = who_view(row.removed_by_name, row.removed_by);
  return {
    key: row.uuid,
    uuid: row.uuid,
    version: row.version,
    teacher_name: row.teacher_name || EM_DASH,
    teacher_uuid: row.teacher_uuid,
    teacher_slug: row.teacher_slug || '',
    month_text: month_label(row.pay_month),
    category_label: row.category_label || row.category,
    category: row.category,
    entry_type: row.entry_type,
    entry_type_label: ENTRY_TYPE_LABELS[row.entry_type] || row.entry_type || EM_DASH,
    amount: num(row.amount),
    amount_text: amount_text(row.amount),
    amount_tone: num(row.amount) < 0 ? 'tp-neg' : 'tp-pos',
    entry_at_text: row.entry_at_ist || EM_DASH,
    note: row.note || '',
    reference_id: row.reference_id || '',
    source: row.source,
    source_label: source_label(row),
    is_system: is_system_row(row),
    is_system_posted: is_system_posted(row),
    source_month_text: row.source_pay_month ? month_label(row.source_pay_month) : '',
    added_text: `${added.text}${row.added_at_ist ? ` · ${row.added_at_ist}` : ''}`,
    added_by_title: added.title,
    status: row.status,
    status_label: STATUS_LABELS[row.status] || row.status || EM_DASH,
    is_removed: row.status === LIST_STATUS.REMOVED,
    removed_by_text: removed.text,
    removed_by_title: removed.title,
    removed_at_text: row.removed_at_ist || '',
    reversed_text: reversed_text(row),
    remove_reason: row.remove_reason || '',
    month_lifecycle: row.month_lifecycle || '',
    can_edit: has_action(row, ROW_ACTION.EDIT),
    can_remove: has_action(row, ROW_ACTION.REMOVE),
    can_waive: has_action(row, ROW_ACTION.WAIVE),
    why_not: (row.editable && row.editable.why_not) || '',
    explain: row.explain === undefined ? null : row.explain,
    row
  };
};

// CD-112: net_total is ACTIVE entries only (what the month pays); removed_total is what Ops removed and
// waived_total the carry-forwards Ops waived, within the filter. A missing total reads as 0.
export const list_view = (data) => {
  const d = data || {};
  const rows = (Array.isArray(d.rows) ? d.rows : []).map(row_view);
  return {
    list_status: 'ok',
    error_text: '',
    pay_month: d.pay_month || '',
    rows,
    raw_rows: Array.isArray(d.rows) ? d.rows : [],
    count: num(d.count),
    total_count: num(d.total_count),
    net_total: num(d.net_total),
    net_total_text: amount_text(d.net_total),
    removed_total: num(d.removed_total),
    waived_total: num(d.waived_total)
  };
};

// A month or filter change in flight: the previous view's count and Net no longer describe the list.
export const pending_list_view = () => ({ ...list_view(null), list_status: 'loading' });

export const REQUEST_FAILED_TEXT = 'The request failed. Try again.';

// Load-failure texts sit beside a "Try again" button, so like the R5b ones they do not say it too.
export const CATEGORIES_LOAD_FAILED_TEXT =
  'Could not load the payment categories, so entries cannot be added or edited.';

export const LIST_LOAD_FAILED_TEXT = 'Could not load the entries.';

// The server's 500 sentences end in "Try again." (CD-144); the button beside them already says it.
export const without_retry = (text) => text.replace(/\s*try again\.?\s*$/i, '');

// Only a LOOKUP_FAILED message is an Ops sentence (CD-144/161); a 400's is developer text.
const load_failed_text = (e, fallback) => {
  const { code, message } = api_error(e);
  return (code === ERROR.LOOKUP_FAILED && without_retry(message)) || fallback;
};

// Without the catalogue neither the add form nor the Edit modal can offer a category.
export const categories_error_text = (e) => load_failed_text(e, CATEGORIES_LOAD_FAILED_TEXT);

// A failed load is not an empty month: no rows, no totals, and the reason in their place.
export const list_error_view = (e) => ({
  ...list_view(null),
  list_status: 'error',
  error_text: load_failed_text(e, LIST_LOAD_FAILED_TEXT)
});

/*
 * Remove copy (the mock's wording). A SYSTEM carry-forward is waived, not removed, and waiving GIVES
 * the money back — the two directions must never read the same in a confirmation dialog.
 */
export const remove_impact_text = (row) => {
  const money = fmt(Math.abs(num(row.amount)));
  if (row.is_system) {
    return `Waiving this carry-forward adds ${money} back to ${row.teacher_name}'s payable for ${row.month_text}.`;
  }
  return `Removing this entry will deduct ${money} from ${row.teacher_name}'s payable for ${row.month_text}.`;
};

const abs_text = (amount) => {
  const n = Math.abs(num(amount));
  return fmt(n, Number.isInteger(n) ? 0 : 2);
};

// status_filter is the APPLIED status filter: removed money is mentioned only when removed rows are listed.
export const list_header = (view, month_text, status_filter) => {
  const title = `Saved entries · ${month_text}`;
  if (view.list_status !== 'ok') return title;
  const n = num(view.total_count);
  const parts = [`${title} · ${n} ${n === 1 ? 'entry' : 'entries'} · Net ${view.net_total_text}`];
  if (status_filter !== LIST_STATUS.ACTIVE) {
    if (num(view.removed_total)) parts.push(`Removed ${abs_text(view.removed_total)}`);
    if (num(view.waived_total)) parts.push(`Waived ${abs_text(view.waived_total)}`);
  }
  return parts.join(' · ');
};

// ---- month writability (what the tab may offer at all) ----

export const CLOSED_MONTH_TEXT =
  'This payment month is closed — reopen it to adjust, then close it again. Entries cannot be added, edited or removed here.';

// The MONTH_CLOSED 409 is about ONE teacher's month; other teachers' months may still be open.
export const TEACHER_MONTH_CLOSED_TEXT =
  "This teacher's month is closed — reopen it to adjust, then close it again.";

/*
 * Q15: only the current and the previous IST pay month may receive an entry. The per-teacher
 * lifecycle is not knowable before a teacher is chosen (no endpoint carries it), so the server's
 * MONTH_CLOSED / MONTH_NOT_OPEN stays the authority; this only hides affordances that can never work.
 * month_badge is the /months aggregate: CLOSED means every teacher-month is closed.
 */
export const month_write_state = ({ pay_month, month_badge, now }) => {
  if (!pay_month) return { can_write: false, why_not: 'Select a payment month.' };
  if (month_badge && month_badge.key === 'CLOSED') {
    return { can_write: false, why_not: CLOSED_MONTH_TEXT };
  }
  if (!is_pay_month_in_window(pay_month, now)) {
    return {
      can_write: false,
      why_not: `Entries can only be added to the current or the previous payment month. ${month_label(
        pay_month
      )} is outside that window — add it to the current month as a "Previous month payment adjustment". Entries already in ${month_label(
        pay_month
      )} can still be edited.`
    };
  }
  return { can_write: true, why_not: '' };
};

// ---- mutation response ----

const net_of = (totals) => (totals && totals.net !== undefined ? num(totals.net) : null);

/*
 * The entry is saved even when the recompute fails (two-transaction design), so the message must
 * never imply the money is final: a failed recompute leaves the month on "needs review".
 */
export const recompute_message = (recompute) => {
  const r = recompute || {};
  if (r.ok === false) {
    return 'Saved; recalculation failed — the month shows "needs review" until it recomputes.';
  }
  if (r.skipped) {
    return `Saved; the month was not recalculated${r.skip_reason ? ` (${r.skip_reason})` : ''}.`;
  }
  const before = net_of(r.totals_before);
  const after = net_of(r.totals_after);
  const net_text = before !== null && after !== null ? ` Net ${fmt(before)} → ${fmt(after)}.` : '';
  const cf =
    num(r.carry_forward) > 0 ? ` ${fmt(r.carry_forward)} will carry forward to next month.` : '';
  return `Saved.${net_text}${cf}`;
};

const WARNING_FALLBACKS = {
  [WARNING.ENTRY_DATE_OUTSIDE_PAY_MONTH]: 'The entry date is outside the payment month.',
  [WARNING.MONTH_WILL_GO_NEGATIVE]: 'This month nets below zero; the deficit carries forward.',
  [WARNING.TEACHER_HAS_NO_BASE_RATE]: 'This teacher has no base rate, so the month cannot compute.',
  [WARNING.RECOMPUTE_FAILED]: 'The month could not be recalculated.',
  [WARNING.ROUTED_TO_CURRENT_MONTH]:
    'The previous month is already closed, so this was added to the current month instead.'
};

export const warnings_view = (warnings) =>
  (Array.isArray(warnings) ? warnings : [])
    .filter((w) => w && w.code)
    .map((w) => ({ code: w.code, message: w.message || WARNING_FALLBACKS[w.code] || w.code }));

/*
 * A replay does not recompute; the month may still be flagged from the first save, or its state may be
 * unreadable. The server's RECOMPUTE_FAILED text names which, so it is the message when present.
 */
const replay_message = (recompute, warnings) => {
  if (!(recompute && recompute.ok === false))
    return 'Already saved — this entry was submitted twice.';
  const server = (Array.isArray(warnings) ? warnings : []).find(
    (w) => w && w.code === WARNING.RECOMPUTE_FAILED && typeof w.message === 'string' && w.message
  );
  return server
    ? server.message
    : 'Already saved — this entry was submitted twice. The month has not been recalculated yet; it shows "needs review" until it recomputes.';
};

export const NOTHING_CHANGED_TEXT = 'Nothing changed — the entry already had these values.';

const mutation_message = (d) => {
  if (d.replayed === true) return replay_message(d.recompute, d.warnings);
  // changed:false = no write, no audit, no recompute on the server; never report it as a save.
  if (d.changed === false) return NOTHING_CHANGED_TEXT;
  return recompute_message(d.recompute);
};

// tone is the banner colour: success | warning (not recalculated) | info (no-op).
export const mutation_result = (data) => {
  const d = data || {};
  const recompute_ok = !(d.recompute && d.recompute.ok === false);
  let tone = recompute_ok ? 'success' : 'warning';
  if (d.changed === false && d.replayed !== true) tone = 'info';
  // When the recompute did not run, the message already says so; RECOMPUTE_FAILED would repeat it.
  const warnings = warnings_view(d.warnings).filter(
    (w) => recompute_ok || w.code !== WARNING.RECOMPUTE_FAILED
  );
  return {
    entry: d.entry || null,
    replayed: d.replayed === true,
    changed: d.changed !== false,
    message: mutation_message(d),
    recompute_ok,
    tone,
    warnings
  };
};

// The shared notifications component styles only success and error.
const NOTIFY_TYPES = { success: 'success', warning: 'error', info: 'success' };

export const notify_type = (tone) => NOTIFY_TYPES[tone] || 'error';

// ---- errors ----

// Codes the operator can act on get our wording; everything else shows the server's message.
const ERROR_TEXT = {
  [ERROR.MONTH_CLOSED]: TEACHER_MONTH_CLOSED_TEXT,
  [ERROR.MONTH_NOT_OPEN]:
    'This teacher-month is not open, so it cannot take an entry. It has to be reopened first.',
  [ERROR.VERSION_CONFLICT]:
    'Someone else changed this entry while it was open. The list has been reloaded — check the entry and try again.',
  [ERROR.ALREADY_REMOVED]: 'This entry was already removed.',
  [ERROR.SYSTEM_ENTRY_NOT_EDITABLE]:
    'A system carry-forward cannot be edited — it can only be waived.',
  [ERROR.DUPLICATE_REFERENCE]: 'Another entry in this month already uses that reference id.',
  [ERROR.PAY_MONTH_OUTSIDE_WINDOW]:
    'Only the current and the previous payment month can take an entry.',
  [ERROR.SESSION_NOT_OWNED_BY_TEACHER]: 'That session does not belong to the selected teacher.',
  [ERROR.TEACHER_NOT_FOUND]: 'That teacher could not be found.',
  [ERROR.ENTRY_NOT_FOUND]: 'That entry no longer exists — reload the list.'
};

export const FIELD_ERROR_TEXT = {
  [ERROR.INVALID_REQUEST]: 'Required.',
  [ERROR.INVALID_AMOUNT]: 'Enter a positive amount with at most 2 decimals.',
  [ERROR.AMOUNT_ABOVE_CAP]: 'Above the per-entry maximum.',
  [ERROR.UNKNOWN_CATEGORY]: 'Choose a category.',
  [ERROR.SYSTEM_CATEGORY_NOT_SELECTABLE]: 'That category is written by the system only.',
  [ERROR.NOTE_REQUIRED]:
    'A note of 1 to 1,024 characters is required — the teacher sees it on the payment email.',
  [ERROR.REASON_REQUIRED]: 'A reason of 1 to 1,024 characters is required.',
  [ERROR.INVALID_ENTRY_DATE]: 'Enter a valid date (and a 24-hour time, if any).',
  [ERROR.INVALID_REFERENCE_ID]: 'Letters, digits and . _ : / - only.',
  [ERROR.RESERVED_REFERENCE_ID]: 'The CF: prefix is reserved for system carry-forwards.',
  [ERROR.PAY_MONTH_OUTSIDE_WINDOW]: 'Outside the editable window.'
};

// Only when an IDEMPOTENCY_MISMATCH arrives without the server's message. It must never say "enter
// it again": the mismatch exists because the earlier save committed.
export const ALREADY_SAVED_TEXT =
  'Your earlier save of this entry went through and was not saved again. Find it in the list and edit it if its values are wrong.';

/*
 * Codes whose server message is the better text (it names the effective cap, the offending field or
 * the month); this wording is only for a body without one. Most are client bugs or races the form
 * already prevents, so the text says what to do rather than what went wrong.
 */
const FALLBACK_TEXT = {
  // Every R8 endpoint's 500; a save's shows UNCERTAIN_SAVE_TEXT and a list load its own text instead.
  [ERROR.LOOKUP_FAILED]: 'Something went wrong on the server. Try again.',
  // A 401/403 reaches the no-permission page by status; this is only for a body without a message.
  [ERROR.FORBIDDEN]: "You don't have access to teacher payments.",
  // GET /additional/list only (a column sort, never a form save); no header-click UI sends one yet,
  // so this is unreachable from the app today — kept for the exhaustive error-code check.
  [ERROR.BAD_SORT]: 'That sort is not available. Reload the page and try again.',
  [ERROR.INVALID_REQUEST]: 'The request was incomplete. Reload the page and try again.',
  [ERROR.INVALID_PAY_MONTH]: 'That payment month is not valid. Pick the month again.',
  [ERROR.AMOUNT_ABOVE_CAP]: 'The amount is above the per-entry maximum.',
  [ERROR.ENTRY_TYPE_NOT_ALLOWED]:
    'Only payments to the teacher go here; deductions go in Penalties.',
  [ERROR.INVALID_CLIENT_REQUEST_ID]:
    'This form lost its request id. Close it and add the entry again.',
  [ERROR.IMMUTABLE_FIELD]:
    'Teacher and payment month cannot be edited — remove this entry and add a new one.',
  [ERROR.INVALID_TEACHER_QUERY]: `Type at least ${LIMITS.TEACHER_QUERY_MIN_LEN} characters to search.`,
  [ERROR.SESSION_NOT_FOUND]: 'That session could not be found. Pick it again.',
  [ERROR.POSSIBLE_DUPLICATE]:
    'This teacher already has an entry in this month with the same category and amount.',
  [ERROR.CONFIRM_REQUIRED]: 'This amount is above the review threshold. Save it anyway?',
  [ERROR.IDEMPOTENCY_MISMATCH]: ALREADY_SAVED_TEXT,
  [ERROR.PREVIOUS_MONTH_NO_RECORD]:
    'This teacher has no payment record for that month. Add this to the current month as a "Previous month payment adjustment" instead.'
};

// FM-4: no answer or a 5xx may still have committed. A create retried with the same request id
// replays, and an edit retried with the old version is refused, so a second press cannot pay twice.
export const UNCERTAIN_SAVE_TEXT =
  'The save may have gone through. Press Save again. It will not pay twice. Or check the list below.';

// The code is never shown raw. existing / mismatch_fields come with IDEMPOTENCY_MISMATCH only.
export const error_view = (e) => {
  const res = (e && e.response) || {};
  const body = res.data || {};
  const { status } = res;
  const { code, message } = api_error(e);
  return {
    code,
    status,
    is_permission: status === 401 || status === 403,
    matches: Array.isArray(body.matches) ? body.matches : [],
    existing: body.existing && typeof body.existing === 'object' ? body.existing : null,
    mismatch_fields: Array.isArray(body.mismatch_fields) ? body.mismatch_fields : [],
    message:
      ERROR_TEXT[code] ||
      message ||
      FALLBACK_TEXT[code] ||
      FIELD_ERROR_TEXT[code] ||
      REQUEST_FAILED_TEXT
  };
};

// For a create/update only: the view of a failure that may have saved (no response, or a 5xx).
export const save_failure_view = (view) => {
  const uncertain = !view.status || view.status >= 500;
  return { ...view, uncertain, message: uncertain ? UNCERTAIN_SAVE_TEXT : view.message };
};

export const is_already_saved_error = (view) => !!view && view.code === ERROR.IDEMPOTENCY_MISMATCH;

// Labels for the server's published mismatch_fields (enums fixture); an unknown one shows raw.
export const MISMATCH_FIELD_LABELS = {
  teacher_uuid: 'Teacher',
  pay_month: 'Payment month',
  category: 'Category',
  entry_type: 'Type',
  amount: 'Amount',
  entry_date: 'Date / time',
  note: 'Note',
  reference_id: 'Reference',
  related_session_uuid: 'Related session'
};

const or_dash = (v) => (v === undefined || v === null || v === '' ? EM_DASH : String(v));

// saved = the committed entry (wire row); sent = the refused create payload + its display names.
const MISMATCH_TEXT = {
  teacher_uuid: (saved, sent) => [
    saved.teacher_name || saved.teacher_uuid,
    sent.teacher_name || sent.teacher_uuid
  ],
  pay_month: (saved, sent) => [month_label(saved.pay_month), month_label(sent.pay_month)],
  category: (saved, sent) => [
    saved.category_label || saved.category,
    sent.category_label || sent.category
  ],
  entry_type: (saved, sent) => [saved.entry_type, sent.entry_type],
  amount: (saved, sent) => [amount_text(saved.amount), amount_text(sent.amount)],
  entry_date: (saved, sent) => [
    saved.entry_at_ist,
    sent.entry_date ? `${sent.entry_date}${sent.entry_time ? ` ${sent.entry_time}` : ''}` : ''
  ],
  note: (saved, sent) => [saved.note, sent.note],
  reference_id: (saved, sent) => [saved.reference_id, sent.reference_id],
  related_session_uuid: (saved, sent) => [saved.related_session_uuid, sent.related_session_uuid]
};

// Q13: teacher and pay_month cannot be edited, so a mismatch on either is fixed by remove + re-add.
// Same published field names the server keys its message off (MismatchNeedsRemove).
export const mismatch_needs_remove = (fields) =>
  Array.isArray(fields) && (fields.includes('teacher_uuid') || fields.includes('pay_month'));

/*
 * What the tab shows after an IDEMPOTENCY_MISMATCH: the server's message, the saved entry (as a list
 * row, so it can be highlighted and edited even before a lagging list shows it), and each differing
 * field as saved-vs-sent.
 */
export const already_saved_view = (view, sent) => {
  const saved = (view && view.existing) || null;
  const out = sent || {};
  return {
    message: (view && view.message) || ALREADY_SAVED_TEXT,
    entry: saved ? row_view(saved) : null,
    needs_remove: !!saved && mismatch_needs_remove(view && view.mismatch_fields),
    differences: saved
      ? ((view && view.mismatch_fields) || []).map((field) => {
          const pick = MISMATCH_TEXT[field];
          const [a, b] = pick ? pick(saved, out) : [saved[field], out[field]];
          return {
            field,
            label: MISMATCH_FIELD_LABELS[field] || field,
            saved_text: or_dash(a),
            sent_text: or_dash(b)
          };
        })
      : []
  };
};

// The two 409s the operator clears with a dialog. They are DIFFERENT dialogs with different retry
// flags: POSSIBLE_DUPLICATE needs confirm_duplicate, CONFIRM_REQUIRED needs confirm_large. Routing
// CONFIRM_REQUIRED into the duplicate handler resubmits the same payload forever (B6).
export const is_duplicate_error = (view) => !!view && view.code === ERROR.POSSIBLE_DUPLICATE;

export const is_confirm_large_error = (view) => !!view && view.code === ERROR.CONFIRM_REQUIRED;

/*
 * B7: an error raised with a modal open must land where the operator is looking. These codes are
 * terminal for the open modal — the row, the month or the entry itself is no longer in a state the
 * modal can act on — so the modal closes and the tab banner + a list reload carry the message.
 * IDEMPOTENCY_MISMATCH too: the dialog's only action re-sends the same request id, a 409 forever
 * (the tab then lands the operator on the saved entry — see already_saved_view). So is
 * PREVIOUS_MONTH_NO_RECORD (CD-113): only moving the entry to the current month can clear it.
 * Everything else (AMOUNT_ABOVE_CAP, DUPLICATE_REFERENCE, any other 400/500) is fixable in place,
 * so it renders inside the modal instead of behind it.
 */
export const TERMINAL_MODAL_ERRORS = [
  ERROR.VERSION_CONFLICT,
  ERROR.ENTRY_NOT_FOUND,
  ERROR.ALREADY_REMOVED,
  ERROR.SYSTEM_ENTRY_NOT_EDITABLE,
  ERROR.MONTH_CLOSED,
  ERROR.MONTH_NOT_OPEN,
  ERROR.IDEMPOTENCY_MISMATCH,
  ERROR.PREVIOUS_MONTH_NO_RECORD
];

export const closes_modal = (view) => !!view && TERMINAL_MODAL_ERRORS.indexOf(view.code) !== -1;

// The surfaces a mutation is sent from; each has its own saving state and error line (RM4).
export const SURFACE = { FORM: 'form', EDIT: 'edit', REMOVE: 'remove', CONFIRM: 'confirm' };

/*
 * Where a failure lands: the surface that sent it while that surface is still open, else the one it
 * sits over (a confirm over the form or the Edit modal), else the tab banner. A terminal refusal
 * closes the dialogs it made stale; the add form is never closed by one. origin/parent: {key, alive}.
 */
export const failure_route = (view, origin, parent = origin) => {
  const surfaces = origin.key === parent.key ? [origin] : [origin, parent];
  const close = closes_modal(view)
    ? surfaces.filter((s) => s.alive && s.key !== SURFACE.FORM).map((s) => s.key)
    : [];
  const open = surfaces.find((s) => s.alive && close.indexOf(s.key) === -1);
  return { place: open ? open.key : 'banner', close };
};

// RL2: the add form closed on a month switch, so there is nothing to "Save again" from.
export const lost_create_text = (view, month_text) =>
  view.uncertain
    ? `The save for ${month_text} may have gone through. Check the ${month_text} list before adding it again.`
    : `The entry for ${month_text} was not saved: ${view.message}`;

// A create's confirm dialog answered after a month switch says which month the save is for.
export const for_month_text = (message, month_text) => `This save is for ${month_text}. ${message}`;

// A failure shown in a still-open dialog after a month switch: the list below is another month's.
export const other_month_failure_text = (view, month_text) =>
  view.uncertain
    ? `The save for ${month_text} may have gone through. Press Save again. It will not pay twice. Or check the ${month_text} list.`
    : for_month_text(view.message, month_text);

export const EARLIER_EDIT_SAVED_TEXT =
  'Your earlier save probably went through; the list is reloaded — check the entry.';

// RL3: after an uncertain edit of this row, a conflict is most likely that edit having committed.
export const edit_failure_view = (view, after_uncertain) =>
  after_uncertain && view.code === ERROR.VERSION_CONFLICT
    ? { ...view, message: EARLIER_EDIT_SAVED_TEXT }
    : view;
